export class SessionManager {
    constructor(engine) {
        this.engine = engine;
        this.events = [];
        this.startTime = 0;
        this.isRecording = false;
        this.isPlaying = false;
        this.playbackTime = 0;
        this.playbackReqId = null;
        this.currentPatch = null;
        
        // Hooks for external control
        this.onPlaybackEnd = null;
        this.onPlaybackUpdate = null; // e.g. for progress bar
    }

    // --- State Capture ---

    capturePatch() {
        // We capture the DOM state because that's the source of truth for the UI
        const knobs = Array.from(document.querySelectorAll('rotary-knob')).map(k => ({
            fx: k.getAttribute('data-fx'),
            param: k.getAttribute('data-param'),
            value: parseFloat(k.value)
        }));

        const waveBtn = document.querySelector('.wave-btn.active');
        const waveform = waveBtn ? waveBtn.dataset.wave : 'sawtooth';

        const masterVol = parseFloat(document.getElementById('master-vol').value);
        
        const lfoTarget = document.getElementById('lfo-target-select').value;
        
        // FX Order
        const fxOrder = Array.from(document.querySelectorAll('.rack-module')).map(el => el.dataset.id);

        // Bypasses
        const bypasses = Array.from(document.querySelectorAll('.bypass-toggle')).map(el => ({
            id: el.closest('.rack-module').dataset.id,
            checked: el.checked
        }));

        return {
            knobs,
            waveform,
            masterVol,
            lfoTarget,
            fxOrder,
            bypasses,
            theme: document.body.className // Capture theme too
        };
    }

    applyPatch(patch) {
        if (!patch) return;

        // Apply Knobs
        if (patch.knobs) {
            patch.knobs.forEach(p => {
                const knob = document.querySelector(`rotary-knob[data-fx="${p.fx}"][data-param="${p.param}"]`);
                if (knob) {
                    knob.value = p.value; // Triggers 'input' event handled by main.js
                }
            });
        }

        // Apply Waveform
        if (patch.waveform) {
            const btn = document.querySelector(`.wave-btn[data-wave="${patch.waveform}"]`);
            if (btn) btn.click();
        }

        // Apply Master Vol
        if (patch.masterVol !== undefined) {
            const vol = document.getElementById('master-vol');
            if (vol) {
                vol.value = patch.masterVol;
                vol.dispatchEvent(new Event('input'));
            }
        }

        // Apply LFO Target
        if (patch.lfoTarget) {
            const sel = document.getElementById('lfo-target-select');
            if (sel) {
                sel.value = patch.lfoTarget;
                sel.dispatchEvent(new Event('change'));
            }
        }
        
        // FX Order (Reordering DOM)
        if (patch.fxOrder) {
            const container = document.getElementById('fx-rack-container');
            const currentModules = Array.from(container.querySelectorAll('.rack-module'));
            // Detach all
            currentModules.forEach(el => el.remove());
            // Reattach in order
            patch.fxOrder.forEach(id => {
                const mod = currentModules.find(el => el.dataset.id === id);
                if (mod) container.appendChild(mod);
            });
            // Update engine routing
            this.engine.updateRouting(patch.fxOrder);
        }

        // Bypasses
        if (patch.bypasses) {
            patch.bypasses.forEach(b => {
                const mod = document.querySelector(`.rack-module[data-id="${b.id}"]`);
                if (mod) {
                    const toggle = mod.querySelector('.bypass-toggle');
                    if (toggle) {
                        toggle.checked = b.checked;
                        toggle.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }
            });
        }

        // Theme
        if (patch.theme) {
            // A bit hacky but works for now
            // We need to sync the theme engine variable in main.js if we want toggle to work correctly
            // For now just applying class is visual enough
            // Ideally we expose setMethod in main.js
            // console.warn("Theme application requires main.js exposure, skipping for now to avoid desync.");
        }
    }

    // --- Recording ---

    startRecording() {
        this.currentPatch = this.capturePatch();
        this.events = [];
        this.startTime = performance.now();
        this.isRecording = true;
        console.log("Recording started...");
    }

    recordEvent(type, data) {
        if (!this.isRecording) return;
        const time = performance.now() - this.startTime;
        this.events.push({
            t: Math.round(time), // Relative time in ms
            type: type,
            data: data
        });
    }

    stopRecording() {
        this.isRecording = false;
        console.log(`Recording stopped. ${this.events.length} events captured.`);
    }

    // --- Playback ---

    play(callbacks) {
        if (this.isPlaying) this.stopPlayback();
        if (!this.currentPatch) return;

        // Reset State first
        this.applyPatch(this.currentPatch);

        this.isPlaying = true;
        this.playbackTime = 0;
        const startTs = performance.now();
        let eventIdx = 0;
        
        // Destructure callbacks for simulated input
        const { onStart, onMove, onEnd, onNoteOn, onNoteOff } = callbacks;

        const loop = () => {
            if (!this.isPlaying) return;
            const now = performance.now();
            const elapsed = now - startTs;

            // Process events due
            while (eventIdx < this.events.length && this.events[eventIdx].t <= elapsed) {
                const e = this.events[eventIdx];
                const id = (e.data && e.data.id !== undefined) ? e.data.id : 'mouse';
                
                // Execute Event
                if (e.type === 'start' && onStart) onStart(e.data.x, e.data.y, id);
                if (e.type === 'move' && onMove) onMove(e.data.x, e.data.y, id);
                if (e.type === 'end' && onEnd) onEnd(id);
                if (e.type === 'noteOn' && onNoteOn) onNoteOn(e.data.freq, e.data.gain, id);
                if (e.type === 'noteOff' && onNoteOff) onNoteOff(id);
                
                eventIdx++;
            }

            // Check end
            if (eventIdx >= this.events.length) {
                // Wait a bit or stop?
                if (elapsed > this.events[this.events.length - 1].t + 1000) {
                    this.stopPlayback();
                    if (this.onPlaybackEnd) this.onPlaybackEnd();
                } else {
                    this.playbackReqId = requestAnimationFrame(loop);
                }
            } else {
                this.playbackReqId = requestAnimationFrame(loop);
            }
        };

        this.playbackReqId = requestAnimationFrame(loop);
        console.log("Playback started...");
    }

    stopPlayback() {
        this.isPlaying = false;
        if (this.playbackReqId) cancelAnimationFrame(this.playbackReqId);
        // Ensure note off
        // this.engine.triggerRelease(); // Should be handled by 'end' event if recorded correctly, but safety is good.
    }

    // --- Serialization ---

    pack() {
        if (!this.currentPatch) return null;
        
        // Group knobs by FX
        const groupedKnobs = {};
        if (this.currentPatch.knobs) {
            this.currentPatch.knobs.forEach(k => {
                if (!groupedKnobs[k.fx]) groupedKnobs[k.fx] = [];
                // Round values to save space
                groupedKnobs[k.fx].push([k.param, parseFloat(k.value.toFixed(4))]);
            });
        }

        const compactPatch = {
            ...this.currentPatch,
            knobs: groupedKnobs
        };
        
        // Compact Events
        // Events: [t, typeCode, x, y] (flattened array)
        // typeCode: 0=start, 1=move, 2=end
        const compactEvents = [];
        let lastT = 0;
        
        this.events.forEach(e => {
            const dt = e.t - lastT;
            lastT = e.t;
            
            let idVal = -1; // Default mouse
            if (e.data && e.data.id !== undefined && e.data.id !== 'mouse') {
                idVal = parseInt(e.data.id);
                if (isNaN(idVal)) idVal = -1; // Fallback
            }
            
            if (e.type === 'start') {
                compactEvents.push(dt, 0, parseFloat(e.data.x.toFixed(4)), parseFloat(e.data.y.toFixed(4)), idVal);
            } else if (e.type === 'move') {
                compactEvents.push(dt, 1, parseFloat(e.data.x.toFixed(4)), parseFloat(e.data.y.toFixed(4)), idVal);
            } else if (e.type === 'end') {
                compactEvents.push(dt, 2, idVal);
            } else if (e.type === 'noteOn') {
                compactEvents.push(dt, 3, parseFloat(e.data.freq.toFixed(2)), parseFloat(e.data.gain.toFixed(2)), idVal);
            } else if (e.type === 'noteOff') {
                compactEvents.push(dt, 4, idVal);
            }
        });

        return {
            v: '1.3', // Update version for Note support
            p: compactPatch,
            e: compactEvents
        };
    }

    unpack(session) {
        if (session.v === '1.2' || session.v === '1.3') {
            const compactPatch = session.p;
            
            // Ungroup Knobs
            const expandedKnobs = [];
            if (compactPatch.knobs && !Array.isArray(compactPatch.knobs)) {
                for (const fx in compactPatch.knobs) {
                    compactPatch.knobs[fx].forEach(paramArr => {
                        expandedKnobs.push({
                            fx: fx,
                            param: paramArr[0],
                            value: paramArr[1]
                        });
                    });
                }
                compactPatch.knobs = expandedKnobs;
            }

            this.currentPatch = compactPatch;
            
            // Expand Events
            this.events = [];
            const arr = session.e;
            let i = 0;
            let currentT = 0;
            
            while(i < arr.length) {
                const dt = arr[i++];
                const typeCode = arr[i++];
                currentT += dt;
                
                if (typeCode === 0) { // Start
                    const x = arr[i++];
                    const y = arr[i++];
                    const idVal = arr[i++];
                    const id = idVal === -1 ? 'mouse' : idVal;
                    this.events.push({ t: currentT, type: 'start', data: {x, y, id} });
                } else if (typeCode === 1) { // Move
                    const x = arr[i++];
                    const y = arr[i++];
                    const idVal = arr[i++];
                    const id = idVal === -1 ? 'mouse' : idVal;
                    this.events.push({ t: currentT, type: 'move', data: {x, y, id} });
                } else if (typeCode === 2) { // End
                    const idVal = arr[i++];
                    const id = idVal === -1 ? 'mouse' : idVal;
                    this.events.push({ t: currentT, type: 'end', data: {id} });
                } else if (typeCode === 3) { // NoteOn
                    const freq = arr[i++];
                    const gain = arr[i++];
                    const idVal = arr[i++];
                    const id = idVal === -1 ? 'mouse' : idVal;
                    this.events.push({ t: currentT, type: 'noteOn', data: {freq, gain, id} });
                } else if (typeCode === 4) { // NoteOff
                    const idVal = arr[i++];
                    const id = idVal === -1 ? 'mouse' : idVal;
                    this.events.push({ t: currentT, type: 'noteOff', data: {id} });
                }
            }
            return true;
        } else if (session.v === '1.1') {
            const compactPatch = session.p;
            
            // Ungroup Knobs
            const expandedKnobs = [];
            if (compactPatch.knobs && !Array.isArray(compactPatch.knobs)) {
                for (const fx in compactPatch.knobs) {
                    compactPatch.knobs[fx].forEach(paramArr => {
                        expandedKnobs.push({
                            fx: fx,
                            param: paramArr[0],
                            value: paramArr[1]
                        });
                    });
                }
                compactPatch.knobs = expandedKnobs; // Replace with expanded array for applyPatch
            }

            this.currentPatch = compactPatch;
            
            // Expand Events
            this.events = [];
            const arr = session.e;
            let i = 0;
            let currentT = 0;
            
            while(i < arr.length) {
                const dt = arr[i++];
                const typeCode = arr[i++];
                currentT += dt;
                
                if (typeCode === 0) { // Start
                    const x = arr[i++];
                    const y = arr[i++];
                    this.events.push({ t: currentT, type: 'start', data: {x, y} });
                } else if (typeCode === 1) { // Move
                    const x = arr[i++];
                    const y = arr[i++];
                    this.events.push({ t: currentT, type: 'move', data: {x, y} });
                } else if (typeCode === 2) { // End
                    this.events.push({ t: currentT, type: 'end', data: null });
                }
            }
            return true;
        } else if (session.v === '1.0') {
            // Legacy format
            this.currentPatch = session.p;
            this.events = session.e;
            return true;
        }
        return false;
    }

    serialize() {
        const session = this.pack();
        if (!session) return null;
        
        // Simple Minify/Compress strategy: Base64
        try {
            return btoa(JSON.stringify(session));
        } catch (e) {
            console.error("Serialization failed", e);
            return null;
        }
    }

    deserialize(str) {
        try {
            const raw = atob(str);
            const session = JSON.parse(raw);
            if (!session.v || !session.p || !session.e) throw new Error("Invalid format");
            
            return this.unpack(session);
        } catch (e) {
            console.error("Deserialization failed", e);
            return false;
        }
    }

    exportToFile(filename = 'sonic-envelope-session.txt') {
        const data = this.serialize();
        if (!data) return false;
        
        const blob = new Blob([data], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return true;
    }
}
